import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { GitBranch, Inbox } from 'lucide-react';
import EmptyState from '@/components/EmptyState';
import type { AuthoredGraph, CreateOutput, PaletteSource } from '../createFlow/types';
import type {
  BoardDestination,
  BoardFeed,
  RouteFilter,
  RoutingBoardModel,
  SelectedBoardItem,
} from './types';
import { filterBoard } from './composeRoutingBoard';
import { feedCardMotion, readPrefersReducedMotion, routeMotion } from './motion';
import { cubicPath, formatCount, laneLabel, relativeTime } from './present';
import './routingBoard.css';

const DRAG_MIME = 'application/x-dc-create-source';

export type BoardAuthoring = {
  scopedAppId: string | null;
  ingest: PaletteSource[];
  iot: PaletteSource[];
  extraDests: CreateOutput[];
  graph: AuthoredGraph;
  saving?: boolean;
  saveError?: string | null;
  onDropSource: (source: PaletteSource, position: { x: number; y: number }) => void;
  onConnect: (sourceNodeId: string, destId: string) => void;
  onRemove: (nodeId: string) => void;
};

export type RoutingBoardViewProps = {
  model: RoutingBoardModel;
  filter: RouteFilter;
  selected?: SelectedBoardItem | null;
  onSelect?: (item: SelectedBoardItem) => void;
  generatedAt?: string | null;
  now?: number;
  reduceMotion?: boolean;
  authoring?: BoardAuthoring | null;
};

type Anchor = { x: number; y: number };

function anchorsEqual(left: Record<string, Anchor>, right: Record<string, Anchor>): boolean {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  for (const key of leftKeys) {
    const a = left[key];
    const b = right[key];
    if (!b || a.x !== b.x || a.y !== b.y) return false;
  }
  return true;
}

function idKey(items: Array<{ id: string }> | undefined): string {
  return (items || []).map((item) => item.id).join('\0');
}

function laneDotClass(lane: BoardFeed['lane']): string {
  return `df-lane-dot--${lane}`;
}

function FeedCard({
  feed,
  selected,
  onSelect,
  now,
  reduceMotion = false,
}: {
  feed: BoardFeed;
  selected: boolean;
  onSelect: () => void;
  now?: number;
  reduceMotion?: boolean;
}) {
  const cardMotion = feedCardMotion(feed.lane, reduceMotion);
  return (
    <button
      type="button"
      data-board-id={feed.id}
      data-testid={`df-feed-${feed.id}`}
      data-motion={cardMotion.motion}
      onClick={onSelect}
      className={`df-feed-card--${feed.lane} ${cardMotion.className} w-full text-left bg-white rounded-lg border px-3 py-2.5 shadow-sm hover:shadow transition ${
        selected ? 'ring-2 ring-blue-500' : ''
      }`.replace(/\s+/g, ' ').trim()}
    >
      <div className="flex items-start gap-2">
        <span className="relative mt-1 flex h-2.5 w-2.5">
          <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${laneDotClass(feed.lane)}`} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-900 truncate">{feed.name}</div>
          {feed.endpoint_id && (
            <div className="text-[11px] text-gray-500 truncate">{feed.endpoint_id}</div>
          )}
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
            <span>{relativeTime(feed.last_success_at, now)}</span>
            {feed.records_24h != null && <span>{formatCount(feed.records_24h)} / 24h</span>}
            {feed.interval_seconds != null && <span>{feed.interval_seconds}s</span>}
          </div>
          {feed.lane === 'broken' && feed.last_error && (
            <div className="mt-1 text-[11px] text-red-700 truncate" title={feed.last_error}>
              {feed.last_error}
            </div>
          )}
        </div>
        <span className={`text-[10px] font-semibold uppercase tracking-wide ${
          feed.lane === 'live' ? 'text-green-700' : feed.lane === 'broken' ? 'text-red-700' : 'text-gray-500'
        }`}>
          {laneLabel(feed.lane)}
        </span>
      </div>
    </button>
  );
}

function DestCard({
  dest,
  selected,
  onSelect,
  observeHint = false,
}: {
  dest: BoardDestination;
  selected: boolean;
  onSelect: () => void;
  observeHint?: boolean;
}) {
  return (
    <button
      type="button"
      data-board-id={dest.id}
      data-testid={`df-dest-${dest.id}`}
      onClick={onSelect}
      className={`w-full text-left bg-white rounded-lg border border-gray-200 px-3 py-2.5 shadow-sm hover:shadow transition ${
        selected ? 'ring-2 ring-blue-500' : ''
      }`}
    >
      <div className="text-[10px] uppercase tracking-wide text-gray-400">
        {dest.kind === 'history_store' ? 'History store' : 'Auto-derived flow'}
        {observeHint ? ' · Observe' : ''}
      </div>
      <div className="text-sm font-medium text-gray-900 truncate">{dest.label}</div>
      {dest.flow_type && (
        <div className="text-[11px] text-gray-500">{dest.flow_type}</div>
      )}
    </button>
  );
}

function ExtraDestCard({
  dest,
  pending,
  onSelect,
}: {
  dest: CreateOutput;
  pending: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      data-board-id={dest.id}
      data-testid={`df-dest-${dest.id}`}
      onClick={onSelect}
      className={`w-full text-left bg-white rounded-lg border px-3 py-2.5 shadow-sm hover:shadow transition ${
        pending ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200'
      }`}
    >
      <div className="text-[10px] uppercase tracking-wide text-gray-400">{dest.store}</div>
      <div className="text-sm font-medium text-gray-900 truncate">{dest.label}</div>
      <div className="text-[11px] text-gray-500 truncate">{dest.path}</div>
      {dest.door === 'down' && dest.door_reason && (
        <div className="text-[11px] text-red-700 truncate">{dest.door_reason}</div>
      )}
    </button>
  );
}

export default function RoutingBoardView({
  model,
  filter,
  selected = null,
  onSelect,
  generatedAt,
  now,
  reduceMotion,
  authoring = null,
}: RoutingBoardViewProps) {
  const reduce = readPrefersReducedMotion(reduceMotion);
  const visible = useMemo(() => filterBoard(model, filter), [model, filter]);
  const boardRef = useRef<HTMLDivElement | null>(null);
  const [anchors, setAnchors] = useState<Record<string, Anchor>>({});
  const [pendingSource, setPendingSource] = useState<string | null>(null);
  const [dragHot, setDragHot] = useState(false);

  const paletteById = useMemo(() => {
    const map = new Map<string, PaletteSource>();
    (authoring?.ingest || []).forEach((item) => map.set(item.id, item));
    (authoring?.iot || []).forEach((item) => map.set(item.id, item));
    return map;
  }, [authoring]);

  const authoredSources = useMemo(
    () => (authoring?.graph.nodes || []).filter((node) => node.kind === 'source'),
    [authoring?.graph.nodes]
  );
  const hasAuthoring = Boolean(authoring?.scopedAppId);
  // PR 758 put a fresh .filter() array in this effect's deps. setAnchors then
  // re-rendered the WHOLE /dataflow page (React #185) — Weather 4-feed never
  // painted, so the header App switch never reached a working board. Do not
  // key this effect on `visible` (new object when the parent rebuilds model).
  const layoutKey = [
    idKey(visible.feeds),
    idKey(visible.destinations),
    idKey(authoredSources),
    idKey(authoring?.graph.edges),
    idKey(authoring?.extraDests),
  ].join('\n');
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  useLayoutEffect(() => {
    const root = boardRef.current;
    if (!root) return;
    let raf = 0;
    let writes = 0;

    const measure = () => {
      const current = visibleRef.current;
      const rootBox = root.getBoundingClientRect();
      const next: Record<string, Anchor> = {};
      root.querySelectorAll<HTMLElement>('[data-board-id]').forEach((el) => {
        const id = el.getAttribute('data-board-id');
        if (!id) return;
        const box = el.getBoundingClientRect();
        const isFeed = current.feeds.some((feed) => feed.id === id);
        const isDest = current.destinations.some((dest) => dest.id === id);
        next[id] = {
          x: isFeed
            ? box.right - rootBox.left
            : isDest
              ? box.left - rootBox.left
              : box.left - rootBox.left + box.width / 2,
          y: box.top - rootBox.top + box.height / 2,
        };
      });
      setAnchors((prev) => {
        if (anchorsEqual(prev, next) || writes > 8) return prev;
        writes += 1;
        return next;
      });
    };

    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    });
    observer.observe(root);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [layoutKey]);

  if (model.feeds.length === 0 && authoredSources.length === 0 && (authoring?.ingest.length || 0) === 0 && (authoring?.iot.length || 0) === 0) {
    return (
      <div data-testid="df-board-empty" className="bg-white rounded-xl border border-gray-200">
        <EmptyState
          icon={Inbox}
          title="No feeds on this seat"
          description="This board only shows real workspace APIs and ingest pipelines. Nothing is configured here yet."
        />
      </div>
    );
  }

  if (visible.feeds.length === 0 && !hasAuthoring) {
    return (
      <div data-testid="df-board-filter-empty" className="bg-white rounded-xl border border-gray-200 py-10 text-center text-sm text-gray-500">
        No {filter} routes on this seat.
      </div>
    );
  }

  const fallbackY = (index: number) => 36 + index * 88;
  const historyIsObserve = visible.destinations.some(
    (dest) => dest.kind === 'history_store' && dest.store === 'api_ingest_history'
  );

  const handlePaletteDragStart = (source: PaletteSource) => (event: React.DragEvent) => {
    event.dataTransfer.setData(DRAG_MIME, source.id);
    event.dataTransfer.effectAllowed = 'copy';
  };

  const handlePlatformDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragHot(false);
    const id = event.dataTransfer.getData(DRAG_MIME);
    const source = paletteById.get(id);
    if (!source || !authoring) return;
    const box = event.currentTarget.getBoundingClientRect();
    authoring.onDropSource(source, {
      x: Math.max(8, event.clientX - box.left - 40),
      y: Math.max(8, event.clientY - box.top - 16),
    });
  };

  return (
    <div
      ref={boardRef}
      className="df-routing-board bg-slate-50 rounded-xl border border-gray-200 p-4"
      data-testid="df-routing-board"
    >
      {hasAuthoring && (
        <div className="mb-4" data-testid="df-create-palette">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Palette
            {authoring?.saving ? ' · Saving…' : ''}
          </h3>
          <p className="text-[11px] text-gray-500 mb-2">
            Header App scopes this palette. Drag an Ingest pipe or IoT Shack device onto Platform, then click source → dest.
          </p>
          {authoring?.saveError && <p className="text-[11px] text-red-700 mb-2">{authoring.saveError}</p>}
          <div className="flex flex-wrap gap-2">
            {(authoring?.ingest || []).map((item) => (
              <button
                key={item.id}
                type="button"
                draggable
                data-testid={`df-palette-${item.id}`}
                onDragStart={handlePaletteDragStart(item)}
                className="df-palette-chip text-left bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs"
              >
                <span className="font-medium text-gray-900">{item.label}</span>
                <span className="ml-2 uppercase tracking-wide text-[10px] text-gray-500">{item.statusLabel}</span>
              </button>
            ))}
            {(authoring?.iot || []).map((item) => (
              <button
                key={item.id}
                type="button"
                draggable
                data-testid={`df-palette-${item.id}`}
                onDragStart={handlePaletteDragStart(item)}
                className="df-palette-chip text-left bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs"
              >
                <span className="font-medium text-gray-900">{item.label}</span>
                <span className="ml-2 uppercase tracking-wide text-[10px] text-gray-500">{item.statusLabel}</span>
              </button>
            ))}
            {(authoring?.ingest.length || 0) === 0 && (authoring?.iot.length || 0) === 0 && (
              <p className="text-xs text-gray-400" data-testid="df-create-empty">
                {authoring?.scopedAppId
                  ? 'No Ingest pipes or IoT Shack devices on this seat.'
                  : 'Select an app in the header to load this seat\'s palette.'}
              </p>
            )}
          </div>
        </div>
      )}

      <svg className="df-routing-board__svg" aria-hidden="true">
        {visible.routes.map((route, index) => {
          const from = anchors[route.fromId] || {
            x: 220,
            y: fallbackY(index),
          };
          const to = anchors[route.toId] || {
            x: 520,
            y: fallbackY(index),
          };
          const d = cubicPath(from.x, from.y, to.x, to.y);
          const selectedRoute = selected?.kind === 'route' && selected.id === route.id;
          const motion = routeMotion(route.lane, reduce);
          return (
            <g key={route.id}>
              <path
                d={d}
                className="df-route-hit"
                onClick={() => onSelect?.({ kind: 'route', id: route.id })}
              />
              <path
                d={d}
                data-testid={`df-route-${route.id}`}
                data-lane={route.lane}
                data-motion={motion.motion}
                className={`df-route df-route--${route.lane} ${motion.className}${selectedRoute ? ' df-route--selected' : ''}`.replace(/\s+/g, ' ').trim()}
              />
            </g>
          );
        })}
        {(authoring?.graph.edges || []).map((edge, index) => {
          const from = anchors[edge.source] || { x: 360, y: fallbackY(index) };
          const to = anchors[edge.target] || { x: 620, y: fallbackY(index) };
          return (
            <path
              key={edge.id}
              data-testid={`df-create-edge-${edge.id}`}
              d={cubicPath(from.x, from.y, to.x, to.y)}
              className="df-route df-route--authored"
            />
          );
        })}
      </svg>

      <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 min-h-[22rem]">
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
            External feeds
          </h3>
          <div className="space-y-2">
            {visible.feeds.map((feed) => (
              <FeedCard
                key={feed.id}
                feed={feed}
                selected={selected?.kind === 'feed' && selected.id === feed.id}
                onSelect={() => onSelect?.({ kind: 'feed', id: feed.id })}
                now={now}
                reduceMotion={reduce}
              />
            ))}
            {visible.feeds.length === 0 && (
              <p className="text-xs text-gray-400">No live ingest reads in this filter. Palette above still lists this seat\'s sources.</p>
            )}
          </div>
        </section>

        <section className="flex flex-col items-center">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3 w-full">
            Platform
          </h3>
          <div
            data-testid="df-platform-drop"
            className={`w-full min-h-[12rem] rounded-xl border-2 border-dashed p-2 ${
              dragHot ? 'border-blue-500 bg-blue-50' : 'border-blue-200 bg-white/60'
            }`}
            onDragOver={(event) => {
              if (!hasAuthoring) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'copy';
              setDragHot(true);
            }}
            onDragLeave={() => setDragHot(false)}
            onDrop={handlePlatformDrop}
          >
            {visible.platform && (
              <button
                type="button"
                data-board-id={visible.platform.id}
                data-testid="df-platform"
                onClick={() => onSelect?.({ kind: 'platform', id: visible.platform!.id })}
                className={`w-full bg-white rounded-xl border border-blue-200 px-4 py-4 text-left shadow-sm ${
                  selected?.kind === 'platform' ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                    <GitBranch className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{visible.platform.label}</div>
                    <div className="text-[11px] text-gray-500">
                      {visible.platform.store || 'ingest store'}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {visible.platform.records_24h == null
                    ? '24h count —'
                    : `${formatCount(visible.platform.records_24h)} last 24h`}
                </div>
                {generatedAt && (
                  <div className="text-[11px] text-gray-400 mt-1">Polled {relativeTime(generatedAt, now)}</div>
                )}
              </button>
            )}
            {!visible.platform && (
              <p className="text-xs text-gray-400 px-2 py-3">Drop a source here to author a flow. Live routes stay the read layer.</p>
            )}
            {hasAuthoring && authoredSources.length === 0 && (
              <p className="text-[11px] text-gray-400 px-2 py-2" data-testid="df-create-box-empty">
                Drop target — drag a palette source onto this Platform column.
              </p>
            )}
            <div className="mt-2 space-y-2">
              {authoredSources.map((node) => (
                <div
                  key={node.id}
                  data-board-id={node.id}
                  data-testid={`df-create-node-${node.id}`}
                  className={`w-full text-left bg-white rounded-lg border px-3 py-2 ${
                    pendingSource === node.id ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200'
                  }`}
                >
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={() => setPendingSource(pendingSource === node.id ? null : node.id)}
                  >
                    <div className="text-[10px] uppercase tracking-wide text-gray-400">{node.class}</div>
                    <div className="text-sm font-medium text-gray-900 truncate">{node.label}</div>
                  </button>
                  <button
                    type="button"
                    className="text-[11px] text-gray-400"
                    onClick={() => authoring?.onRemove(node.id)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
            Destinations
          </h3>
          {visible.destinations.length === 0 && (authoring?.extraDests.length || 0) === 0 ? (
            <p className="text-xs text-gray-400">No history store or auto-derived ingest flows on this seat.</p>
          ) : (
            <div className="space-y-2">
              {visible.destinations.map((dest) => (
                <DestCard
                  key={dest.id}
                  dest={dest}
                  selected={selected?.kind === 'destination' && selected.id === dest.id}
                  observeHint={dest.kind === 'history_store' && historyIsObserve}
                  onSelect={() => {
                    if (pendingSource && dest.kind === 'history_store') {
                      authoring?.onConnect(pendingSource, dest.id);
                      setPendingSource(null);
                      return;
                    }
                    onSelect?.({ kind: 'destination', id: dest.id });
                  }}
                />
              ))}
              {(authoring?.extraDests || []).map((dest) => (
                <ExtraDestCard
                  key={dest.id}
                  dest={dest}
                  pending={Boolean(pendingSource)}
                  onSelect={() => {
                    if (pendingSource) {
                      authoring?.onConnect(pendingSource, dest.id);
                      setPendingSource(null);
                      return;
                    }
                    onSelect?.({ kind: 'destination', id: dest.id });
                  }}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
